import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Activity, AlertCircle, Shield, Clock } from 'lucide-react';

interface PatternEvent {
  id: number;
  type: 'sequential' | 'velocity' | 'datacenter' | 'email' | 'zip' | 'ml';
  icon: string;
  message: string;
  time: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  details: {
    ip?: string;
    location?: string;
    count?: number;
    pattern?: string;
  };
  timestamp: Date;
}

export const LivePatternFeed: React.FC = () => {
  const [events, setEvents] = useState<PatternEvent[]>([]);
  const [isLive, setIsLive] = useState(true);

  const initialEvents: PatternEvent[] = [
    {
      id: 1,
      type: 'sequential',
      icon: '📱',
      message: 'Sequential phone detected: 2262433178',
      time: '2 sec ago',
      severity: 'high',
      details: { pattern: '2262433176-2262433178', count: 3 },
      timestamp: new Date(Date.now() - 2000)
    },
    {
      id: 2,
      type: 'velocity',
      icon: '⚡',
      message: 'Velocity attack: 3 submissions in 45 seconds',
      time: '15 sec ago',
      severity: 'critical',
      details: { ip: '169.197.85.172', count: 3 },
      timestamp: new Date(Date.now() - 15000)
    },
    {
      id: 3,
      type: 'datacenter',
      icon: '🖥️',
      message: 'Data center IP detected: 207.244.89.166',
      time: '1 min ago',
      severity: 'medium',
      details: { ip: '207.244.89.166', location: 'Virginia, US' },
      timestamp: new Date(Date.now() - 60000)
    },
    {
      id: 4,
      type: 'email',
      icon: '📧',
      message: 'Suspicious email domain: newdomain.com',
      time: '2 min ago',
      severity: 'low',
      details: { pattern: '*.newdomain.com' },
      timestamp: new Date(Date.now() - 120000)
    },
    {
      id: 5,
      type: 'zip',
      icon: '📍',
      message: 'Zip concentration spike: 80112 (35% of leads)',
      time: '3 min ago',
      severity: 'high',
      details: { location: 'Englewood, CO', count: 23 },
      timestamp: new Date(Date.now() - 180000)
    },
    {
      id: 6,
      type: 'ml',
      icon: '🤖',
      message: 'ML model flagged high-risk pattern combination',
      time: '4 min ago',
      severity: 'critical',
      details: { pattern: 'Sequential + DataCenter + Email' },
      timestamp: new Date(Date.now() - 240000)
    }
  ];

  useEffect(() => {
    setEvents(initialEvents);

    if (!isLive) return;

    const interval = setInterval(() => {
      // Simulate new events
      const eventTypes = ['sequential', 'velocity', 'datacenter', 'email', 'zip', 'ml'] as const;
      const severities = ['critical', 'high', 'medium', 'low'] as const;
      const icons = ['📱', '⚡', '🖥️', '📧', '📍', '🤖'];
      const messages = [
        'Sequential phone detected: 226243317X',
        'Velocity spike detected from IP range',
        'New data center IP identified',
        'Suspicious email pattern emerging',
        'Geographic concentration alert',
        'ML confidence threshold exceeded'
      ];

      const newEvent: PatternEvent = {
        id: Date.now(),
        type: eventTypes[Math.floor(Math.random() * eventTypes.length)],
        icon: icons[Math.floor(Math.random() * icons.length)],
        message: messages[Math.floor(Math.random() * messages.length)],
        time: 'just now',
        severity: severities[Math.floor(Math.random() * severities.length)],
        details: {
          ip: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
          count: Math.floor(Math.random() * 10) + 1
        },
        timestamp: new Date()
      };

      setEvents(prev => [newEvent, ...prev.slice(0, 9)]); // Keep last 10 events
    }, 8000); // New event every 8 seconds

    return () => clearInterval(interval);
  }, [isLive]);

  useEffect(() => {
    // Update time labels
    const timeInterval = setInterval(() => {
      setEvents(prev => prev.map(event => ({
        ...event,
        time: formatTimeAgo(event.timestamp)
      })));
    }, 10000); // Update every 10 seconds

    return () => clearInterval(timeInterval);
  }, []);

  const formatTimeAgo = (timestamp: Date) => {
    const seconds = Math.floor((new Date().getTime() - timestamp.getTime()) / 1000);
    
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'border-l-red-500 bg-red-50';
      case 'high':
        return 'border-l-orange-500 bg-orange-50';
      case 'medium':
        return 'border-l-yellow-500 bg-yellow-50';
      default:
        return 'border-l-blue-500 bg-blue-50';
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'high':
        return <Shield className="w-4 h-4 text-orange-500" />;
      case 'medium':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Activity className="w-4 h-4 text-blue-500" />;
    }
  };

  const criticalCount = events.filter(e => e.severity === 'critical').length;
  const highCount = events.filter(e => e.severity === 'high').length;

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-green-500" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Live Pattern Detection</h3>
            <p className="text-sm text-muted-foreground">Real-time fraud pattern monitoring</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {(criticalCount > 0 || highCount > 0) && (
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-600 font-medium">
                {criticalCount + highCount} high-priority alerts
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <span className="text-sm text-muted-foreground">
              {isLive ? 'Live' : 'Paused'}
            </span>
          </div>
        </div>
      </div>

      {/* Event Feed */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {events.map((event) => (
          <div key={event.id} className={`border-l-4 p-4 rounded-lg ${getSeverityColor(event.severity)} transition-all duration-300 hover:shadow-md`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <span className="text-xl">{event.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {getSeverityIcon(event.severity)}
                    <span className="text-sm font-medium text-foreground">{event.message}</span>
                  </div>
                  {event.details && (
                    <div className="text-xs text-muted-foreground space-y-1">
                      {event.details.ip && (
                        <div>IP: <span className="font-mono">{event.details.ip}</span></div>
                      )}
                      {event.details.location && (
                        <div>Location: {event.details.location}</div>
                      )}
                      {event.details.count && (
                        <div>Count: {event.details.count}</div>
                      )}
                      {event.details.pattern && (
                        <div>Pattern: {event.details.pattern}</div>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground mt-2">{event.time}</div>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityBadge(event.severity)}`}>
                {event.severity.toUpperCase()}
              </span>
            </div>
          </div>
        ))}

        {events.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No recent pattern detections</p>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t">
        <div className="text-center">
          <div className="text-xl font-bold text-red-600">{criticalCount}</div>
          <div className="text-xs text-muted-foreground">Critical</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-orange-600">{highCount}</div>
          <div className="text-xs text-muted-foreground">High Priority</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-blue-600">{events.length}</div>
          <div className="text-xs text-muted-foreground">Total Events</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-green-600">
            {isLive ? '✓' : '⏸'}
          </div>
          <div className="text-xs text-muted-foreground">Status</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-center mt-4">
        <button
          onClick={() => setIsLive(!isLive)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isLive 
              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
              : 'bg-green-100 text-green-700 hover:bg-green-200'
          }`}
        >
          {isLive ? 'Pause Feed' : 'Resume Feed'}
        </button>
      </div>
    </Card>
  );
};